import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Trash2, ExternalLink, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { ServiceReportsModal } from "./ServiceReportsModal";
import { DeleteServiceDialog } from "./DeleteServiceDialog";
import { PollUsersDialog } from "./PollUsersDialog";
import { ServiceWithReports } from "@/types/admin";

interface ServicesManagementSectionProps {
  services: ServiceWithReports[];
  servicesLoading: boolean;
  onServicesUpdate: (updatedServices: ServiceWithReports[]) => void;
  onRefreshServices?: () => void;
}

export const ServicesManagementSection = ({ 
  services, 
  servicesLoading, 
  onServicesUpdate,
  onRefreshServices 
}: ServicesManagementSectionProps) => {
  const [selectedService, setSelectedService] = useState<ServiceWithReports | null>(null);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPollDialog, setShowPollDialog] = useState(false);

  const getProviderName = (profiles: ServiceWithReports["profiles"]) => {
    if (!profiles) return "Unknown Provider";
    
    const firstName = profiles.first_name || "";
    const lastName = profiles.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();
    
    return fullName || "Unknown Provider";
  };

  const handleViewReports = (service: ServiceWithReports) => {
    setSelectedService(service);
    setShowReportsModal(true);
  };

  const handleDeleteService = (service: ServiceWithReports) => {
    setSelectedService(service);
    setShowDeleteDialog(true);
  };

  const handlePollUsers = (service: ServiceWithReports) => {
    setSelectedService(service);
    setShowPollDialog(true);
  };

  const handleServiceDeleted = () => {
    if (selectedService) {
      onServicesUpdate(services.filter(s => s.id !== selectedService.id));
    }
  };

  const handlePollCreated = () => {
    if (selectedService) {
      onServicesUpdate(services.map(s => 
        s.id === selectedService.id 
          ? { ...s, has_active_poll: true }
          : s
      ));
    }
  };

  const displayedServices = services.slice(0, 8);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-poppins text-2xl">
                All Services
              </CardTitle>
              <CardDescription className="font-inter">
                Monitor and moderate services (flagged services shown first)
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {onRefreshServices && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={onRefreshServices}
                  disabled={servicesLoading}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${servicesLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              )}
              <Link to="/admin-services">
                <Button variant="outline" className="flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" />
                  View All Services
                </Button>
              </Link>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {servicesLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-anyskill-purple" />
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 font-inter mb-2">
                No services found.
              </p>
              <p className="text-sm text-gray-400 font-inter">
                Check the console for detailed logs if this seems incorrect.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {displayedServices.map((service) => (
                  <Card key={service.id} className="border border-gray-200 h-fit">
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div>
                          <h3 className="font-semibold text-sm font-poppins mb-1 line-clamp-2">
                            {service.title}
                          </h3>
                          <p className="text-xs text-gray-600 font-inter truncate">
                            {getProviderName(service.profiles)}
                          </p>
                          {service.description && (
                            <p className="text-xs text-gray-500 font-inter mt-1 line-clamp-2">
                              {service.description}
                            </p>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-1">
                          {service.report_count > 0 ? (
                            <Badge variant="destructive" className="text-xs">
                              {service.report_count} report{service.report_count !== 1 ? 's' : ''}
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">No reports</Badge>
                          )}
                          {service.has_active_poll && (
                            <Badge variant="outline" className="text-xs">
                              Poll Active
                            </Badge>
                          )}
                        </div>
                        
                        <div className="text-xs text-gray-600 font-inter space-y-1">
                          <div className="truncate">
                            {service.service_categories?.name || 'Uncategorized'}
                          </div>
                          <div>
                            ${service.price} ({service.pricing_type})
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2 pt-2 border-t border-gray-100">
                          {service.report_count > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewReports(service)}
                              className="text-xs h-7"
                            >
                              Reports
                            </Button>
                          )}
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePollUsers(service)}
                              disabled={service.has_active_poll}
                              className="text-xs h-7 flex-1"
                            >
                              <Users className="h-3 w-3 mr-1" />
                              Poll
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteService(service)}
                              className="text-xs h-7 flex-1"
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <div className="text-center pt-4 border-t border-gray-100">
                <Link to="/admin-services">
                  <Button variant="outline" className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4" />
                    View All {services.length} Services
                  </Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      {selectedService && (
        <>
          <ServiceReportsModal
            service={selectedService}
            open={showReportsModal}
            onOpenChange={setShowReportsModal}
          />
          
          <DeleteServiceDialog
            service={selectedService}
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            onDeleted={handleServiceDeleted}
          />
          
          <PollUsersDialog
            service={selectedService}
            open={showPollDialog}
            onOpenChange={setShowPollDialog}
            onPollCreated={handlePollCreated}
          />
        </>
      )}
    </>
  );
};
